/** The person's menu — card C-A, gate 32: *"No user menu, no search, no breadcrumbs."*
 *
 * Holds, in this order: the person's name and role · **Profile** · **Settings** · **Help** · **Sign
 * out** (`BUILD/contracts.md` §2). It replaces the sign-out button that sat beside the name, and
 * **sign-out does exactly what that button did** (`useLogout().mutate()`, disabled while pending):
 * the mutation clears the whole query cache and answers "nobody" synchronously, which is what stops
 * the next person on a shared machine seeing this one's figures. Moving the button into a menu must
 * not change a single thing it does (`qa/KEEP.md`, the shell).
 *
 * **The role is still in the bar when the menu is shut.** `SessionBadge` put it there because it
 * answers "why is that button disabled?", and a menu that hid it behind a click would take the
 * answer away from the moment it is wanted. It is also the element `ui-check`'s `signIn` waits on.
 *
 * **Keyboard, per the menu-button pattern** (WAI-ARIA APG):
 *
 * - Enter, Space or ArrowDown on the trigger opens the menu with focus on the first item;
 *   ArrowUp opens it on the last.
 * - ArrowUp, ArrowDown, Home and End move between items, wrapping.
 * - **Escape closes it and puts focus back on the trigger** (design rule 10: a control that
 *   unmounts the thing focus is inside has to give focus somewhere, and the somewhere is what
 *   opened it).
 * - Tab closes it and lets focus move on; a click outside closes it and leaves focus alone.
 *
 * Nothing here is role-gated: every role has a name, a role and a profile.
 */

import type { Icon } from "@phosphor-icons/react";
import { Fingerprint, Gear, Question, SignOut, User } from "@phosphor-icons/react";
import { motion, useReducedMotion } from "motion/react";
import { Link } from "@tanstack/react-router";
import type { KeyboardEvent } from "react";
import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";

import type { Me } from "../api/auth";
import { useLogout } from "../api/auth";
import styles from "./UserMenu.module.css";

const ROLE_LABEL: Record<string, string> = {
  admin: "Admin",
  approver: "Approver",
  contributor: "Contributor",
  viewer: "Viewer",
  auditor: "Auditor",
};

/** The menu items that can be focused, in document order. */
/** Up to two initials from a display name, for the mark beside it. Letters only, by code point, so
 * a name that starts with a digit, a bracket or an emoji falls back to a neutral dot rather than
 * printing something unreadable. It is decoration (`aria-hidden`); the name beside it is the text. */
function initials(name: string): string {
  const words = name.split(/\s+/).filter((word) => /^\p{L}/u.test(word));
  const first = words[0]?.match(/^\p{L}/u)?.[0] ?? "";
  const last = words.length > 1 ? (words[words.length - 1]?.match(/^\p{L}/u)?.[0] ?? "") : "";
  return (first + last).toUpperCase() || "\u00b7";
}

/** The menu's icons use the shell's one weight, `regular` (ADR-0089): nothing in a menu is "the
 * current one", so there is no filled state to show. Decoration only: the words are the label. */
function MenuIcon({ of: Glyph }: { of: Icon }) {
  return <Glyph className={styles.itemIcon} size={16} weight="regular" aria-hidden="true" />;
}

function items(menu: HTMLElement | null): HTMLElement[] {
  return menu ? Array.from(menu.querySelectorAll<HTMLElement>('[role="menuitem"]')) : [];
}

export function UserMenu({ me }: { me: Me }) {
  const logout = useLogout();
  const reduce = useReducedMotion();
  const menuId = useId();
  const [open, setOpen] = useState(false);
  /** Where focus lands when the menu opens. A ref, because it is read by an effect and not
   * rendered. `"last"` is for ArrowUp on the trigger. */
  const landOn = useRef<"first" | "last">("first");
  const wrap = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const menu = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ left: number; top: number; maxHeight: number } | null>(null);

  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      if (!trigger.current || !menu.current) return;
      const anchor = trigger.current.getBoundingClientRect();
      const popup = menu.current.getBoundingClientRect();
      const margin = 8;
      const gap = 8;
      const width = Math.min(menu.current.offsetWidth || popup.width || 210, Math.max(0, window.innerWidth - margin * 2));
      const height = menu.current.scrollHeight || popup.height;
      const below = Math.max(0, window.innerHeight - margin - Math.max(margin, anchor.bottom + gap));
      const above = Math.max(0, Math.min(window.innerHeight - margin, anchor.top - gap) - margin);
      const useBelow = below >= height || below >= above;
      const maxHeight = useBelow ? below : above;
      const top = Math.max(margin, Math.min(
        useBelow ? anchor.bottom + gap : anchor.top - gap - Math.min(height, maxHeight),
        window.innerHeight - margin - Math.min(height, maxHeight),
      ));
      const left = Math.max(margin, Math.min(anchor.right - width, window.innerWidth - margin - width));
      setPosition((previous) => previous?.left === left && previous.top === top && previous.maxHeight === maxHeight
        ? previous : { left, top, maxHeight });
    };
    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(place);
    if (trigger.current) observer?.observe(trigger.current);
    if (menu.current) observer?.observe(menu.current);
    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
      observer?.disconnect();
    };
  }, [open]);

  // Focus moves into the menu on the commit where it exists — the first moment there is anything
  // to focus.
  useEffect(() => {
    if (!open || !position || menu.current?.contains(document.activeElement)) return;
    const list = items(menu.current);
    (landOn.current === "last" ? list[list.length - 1] : list[0])?.focus();
  }, [open, position]);

  // A click anywhere else closes it. `mousedown` rather than `click`, so the click that lands on
  // another control still reaches it; focus is left where the click put it.
  useEffect(() => {
    if (!open) return;
    const away = (event: MouseEvent) => {
      if (wrap.current && !wrap.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  const openOn = (where: "first" | "last") => {
    landOn.current = where;
    setOpen(true);
  };

  /** Close, and give focus back to the trigger. */
  const closeToTrigger = () => {
    setOpen(false);
    trigger.current?.focus();
  };

  const onTriggerKey = (event: KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      openOn("first");
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      openOn("last");
    }
  };

  const onMenuKey = (event: KeyboardEvent<HTMLDivElement>) => {
    const list = items(menu.current);
    const at = list.indexOf(document.activeElement as HTMLElement);
    switch (event.key) {
      case "Escape":
        event.preventDefault();
        closeToTrigger();
        break;
      case "Tab":
        // Not prevented: focus moves on, and the menu is not left open behind it.
        setOpen(false);
        break;
      case "ArrowDown":
        event.preventDefault();
        list[(at + 1) % list.length]?.focus();
        break;
      case "ArrowUp":
        event.preventDefault();
        list[(at - 1 + list.length) % list.length]?.focus();
        break;
      case "Home":
        event.preventDefault();
        list[0]?.focus();
        break;
      case "End":
        event.preventDefault();
        list[list.length - 1]?.focus();
        break;
      default:
    }
  };

  const role = ROLE_LABEL[me.role] ?? me.role;

  return (
    <div className={styles.wrap} ref={wrap}>
      <button
        ref={trigger}
        type="button"
        className={styles.trigger}
        data-testid="user-menu"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        onClick={() => (open ? setOpen(false) : openOn("first"))}
        onKeyDown={onTriggerKey}
      >
        <span className={styles.avatar} aria-hidden="true">
          {initials(me.display_name)}
        </span>
        <span className={styles.who}>
          <span className={styles.name}>{me.display_name}</span>
          <span className={styles.role}>{role}</span>
        </span>
        <span className={styles.caret} aria-hidden="true" />
      </button>

      {open ? (
        // **Slides in; does not slide out.** The entrance confirms that the press opened something.
        // An exit would keep the items in the document for its duration, and "Escape closed it"
        // is a fact the gate and the tests read immediately: a menu that is still there 150ms
        // after it was dismissed is one a fast keyboard user can act on. Opacity and transform
        // only; nothing at all under reduced motion.
        <motion.div
          initial={reduce ? false : { opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: reduce ? 0 : 0.12, ease: "easeOut" }}
          id={menuId}
          ref={menu}
          role="menu"
          aria-label="Account"
          className={styles.menu}
          style={position ?? undefined}
          data-testid="user-menu-list"
          onKeyDown={onMenuKey}
        >
          {/* Who this is and in what role, first — read, not chosen. A group rather than loose
              text, because a `menu` may contain items, groups and separators and nothing else. */}
          <div role="group" aria-label="Signed in as" className={styles.identity}>
            <span className={styles.identityName}>{me.display_name}</span>
            <span className={styles.identityKind}>{role}</span>
          </div>

          <Link
            to="/profile"
            // Whatever the address already says — the method and the reporting window — travels
            // with the reader, as it does on the rail, so a visit here does not reset the year.
            search={(prev) => ({ ...prev, method: prev.method ?? "location" })}
            role="menuitem"
            tabIndex={-1}
            className={styles.item}
            data-testid="menu-profile"
            onClick={closeToTrigger}
          >
            <MenuIcon of={User} />
            My profile
          </Link>
          <Link
            to="/my-settings"
            // Whatever the address already says — the method and the reporting window — travels
            // with the reader, as it does on the rail, so a visit here does not reset the year.
            search={(prev) => ({ ...prev, method: prev.method ?? "location" })}
            role="menuitem"
            tabIndex={-1}
            className={styles.item}
            data-testid="menu-settings"
            onClick={closeToTrigger}
          >
            <MenuIcon of={Gear} />
            My settings
          </Link>
          {/* **Beside Settings and before Help** (ADR-0077). This is a person asking about
              their *own* data, which is why it is in this menu and not on the rail: the rail is
              the workspace's work, and a subject request is not work the workspace does — the
              same argument that puts Profile here.

              **It is in the menu because it was in nothing.** The route was wired and left out of
              every list, so `grep -rn privacy-requests frontend/src` returned exactly one line —
              its own `createRoute` — and the screen was reachable only by typing the URL. That is
              the second route found that way on 2026-10-23, after `/scope-3`, which is enough
              for the rule to be written down: **a route is not a feature.** Gate 9 was credited
              partly on this screen while nobody could reach it.

              Not role-gated, deliberately. Every member has data rights, and ADR-0077's intake is
              requester-only — a person records a request about themselves and reads their own
              history, so there is nothing here an Admin may see and a Viewer may not. */}
          <Link
            to="/privacy-requests"
            // Whatever the address already says — the method and the reporting window — travels
            // with the reader, as it does on the rail, so a visit here does not reset the year.
            search={(prev) => ({ ...prev, method: prev.method ?? "location" })}
            role="menuitem"
            tabIndex={-1}
            className={styles.item}
            data-testid="menu-privacy-requests"
            onClick={closeToTrigger}
          >
            <MenuIcon of={Fingerprint} />
            Your data requests
          </Link>
          <Link
            to="/help"
            // Whatever the address already says — the method and the reporting window — travels
            // with the reader, as it does on the rail, so a visit here does not reset the year.
            search={(prev) => ({ ...prev, method: prev.method ?? "location" })}
            role="menuitem"
            tabIndex={-1}
            className={styles.item}
            data-testid="help-link"
            onClick={closeToTrigger}
          >
            <MenuIcon of={Question} />
            Help
          </Link>
          <button
            type="button"
            role="menuitem"
            tabIndex={-1}
            className={`${styles.item} ${styles.signOut}`}
            data-testid="sign-out"
            // Exactly what the button beside the name did. The menu closes first so it is not left
            // open over a screen that is about to become the sign-in form.
            onClick={() => {
              setOpen(false);
              logout.mutate();
            }}
            disabled={logout.isPending}
          >
            <MenuIcon of={SignOut} />
            Sign out
          </button>
        </motion.div>
      ) : null}
    </div>
  );
}
